import React, { useEffect, useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Alert,
  Skeleton,
  Collapse,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useDeleteUnitMutation,
  useMoveUnitMutation,
  useGetUnitsByCourseIdQuery,
} from '../../units/unitsApi';
import {
  useDeleteLessonMutation,
  useMoveLessonMutation,
  useGetLessonsByUnitIdQuery,
} from '../../lessons/lessonsApi';
import { useConfirm } from '../../../shared/components/ConfirmProvider';
import { useObjectPermissionState } from '../../../shared/hooks/useCanEdit';
import ClampedTextTooltip from '../../../shared/components/ClampedTextTooltip';
import {
  SortableList,
  type SortableListRenderProps,
} from '../../../shared/components/SortableList';
import type { ReorderPlacement } from '../../../shared/utils/reorder';
import UnitDialog from '../../units/components/UnitDialog';
import LessonDialog from '../../lessons/components/LessonDialog';

import { Lesson } from '@/shared/types/lesson';
import { Unit } from '@/shared/types/unit';
import { Course } from '@/shared/types/course';

interface CurriculumNavTreeProps {
  course: Course;
  canEditCourse: boolean;
  currentUserId: string;
}

/** Pull a human-readable message out of an unknown RTK Query / thrown error. */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'error' in err) {
    return String(err.error ?? fallback);
  }
  return fallback;
}

const sortByPosition = <T extends { position?: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/**
 * Professional, expandable nav tree for the whole course → unit → lesson
 * hierarchy. Shown behind the `curriculumNavTree` feature flag as an
 * alternative to the legacy one-level-at-a-time drill-down nav.
 */
const CurriculumNavTree: React.FC<CurriculumNavTreeProps> = ({
  course,
  canEditCourse,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const { unitId: activeUnitId, lessonId: activeLessonId } = useParams<{
    unitId?: string;
    lessonId?: string;
  }>();
  const courseId = course.id;

  const {
    data: units = [],
    error: unitsError,
    isLoading: unitsLoading,
  } = useGetUnitsByCourseIdQuery(courseId);
  const [moveUnit] = useMoveUnitMutation();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  // Which unit a new lesson is being created under (null = dialog closed).
  const [addLessonUnitId, setAddLessonUnitId] = useState<string | null>(null);

  const sortedUnits = sortByPosition(units);
  const courseIsActive = !activeUnitId && !activeLessonId;

  const handleMoveUnit = async (unitId: string, placement: ReorderPlacement) => {
    await moveUnit({ unitId, courseId, placement }).unwrap();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Back link */}
      <ListItemButton
        onClick={() => navigate('/curriculum/courses')}
        dense
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 0.25,
        }}
      >
        <ChevronLeftIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.25 }} />
        <Typography color="text.secondary" fontWeight={700} noWrap>
          My Courses
        </Typography>
      </ListItemButton>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mx: 1, mt: 1, fontSize: '0.75rem' }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 0.5 }}>
        <List dense disablePadding sx={{ px: 0.5 }}>
          {/* Course root */}
          <ListItemButton
            selected={courseIsActive}
            onClick={() => navigate(`/curriculum/courses/${courseId}`)}
            sx={{ borderRadius: 1, mb: 0.25, pl: 1 }}
          >
            <MenuBookOutlinedIcon
              fontSize="small"
              sx={{
                mr: 1,
                color: courseIsActive ? 'primary.main' : 'text.secondary',
                flexShrink: 0,
              }}
            />
            <ListItemText
              sx={{ my: 0, minWidth: 0 }}
              disableTypography
              primary={
                <ClampedTextTooltip
                  text={course.title || 'Untitled course'}
                  lineClamp={2}
                  sx={{ fontWeight: 700, lineHeight: 1.35 }}
                />
              }
            />
          </ListItemButton>

          {/* Units */}
          {unitsLoading ? (
            <Box sx={{ pl: 2, pr: 1 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} height={32} sx={{ mb: 0.5, borderRadius: 1 }} />
              ))}
            </Box>
          ) : unitsError ? (
            <Alert severity="error" sx={{ mx: 1, my: 1 }}>
              Failed to load units.
            </Alert>
          ) : sortedUnits.length === 0 ? (
            <Box sx={{ pl: 4, pr: 1, py: 1 }}>
              <Typography variant="body2" color="text.disabled">
                No units yet
              </Typography>
            </Box>
          ) : (
            <SortableList
              items={sortedUnits}
              canEdit={canEditCourse}
              onMove={handleMoveUnit}
              getItemLabel={(unit) => `Reorder ${unit.title || 'Untitled unit'}`}
              reorderErrorMessage="Failed to reorder unit. Please try again."
              renderItem={(unit, dragProps) => (
                <UnitTreeNode
                  unit={unit}
                  index={dragProps.index}
                  courseId={courseId}
                  canEditCourse={canEditCourse}
                  activeUnitId={activeUnitId}
                  activeLessonId={activeLessonId}
                  onAddLesson={(uid) => setAddLessonUnitId(uid)}
                  onError={setErrorMsg}
                  dragProps={dragProps}
                />
              )}
            />
          )}

          {/* Add unit */}
          {canEditCourse && !unitsLoading && (
            <ListItemButton
              onClick={() => setUnitDialogOpen(true)}
              sx={{ borderRadius: 1, mt: 0.25, pl: 1, color: 'text.secondary' }}
            >
              <AddIcon fontSize="small" sx={{ mr: 1, flexShrink: 0 }} />
              <ListItemText
                primary="New Unit"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              />
            </ListItemButton>
          )}
        </List>
      </Box>

      {/* Create-unit dialog */}
      {canEditCourse && (
        <UnitDialog
          open={unitDialogOpen}
          onClose={() => setUnitDialogOpen(false)}
          courseId={courseId}
          currentUserId={currentUserId}
          mode="create"
        />
      )}

      {/* Create-lesson dialog (scoped to whichever unit triggered it) */}
      {addLessonUnitId && (
        <LessonDialog
          open
          onClose={() => setAddLessonUnitId(null)}
          unitId={addLessonUnitId}
          currentUserId={currentUserId}
          mode="create"
        />
      )}
    </Box>
  );
};

interface UnitTreeNodeProps {
  unit: Unit;
  index: number;
  courseId: string;
  canEditCourse: boolean;
  activeUnitId?: string;
  activeLessonId?: string;
  onAddLesson: (unitId: string) => void;
  onError: (message: string) => void;
  dragProps: SortableListRenderProps;
}

const UnitTreeNode: React.FC<UnitTreeNodeProps> = ({
  unit,
  index,
  courseId,
  canEditCourse,
  activeUnitId,
  activeLessonId,
  onAddLesson,
  onError,
  dragProps,
}) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isActiveUnit = activeUnitId === unit.id;
  // Auto-expand the unit that contains the active unit/lesson.
  const [expanded, setExpanded] = useState(isActiveUnit);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [deleteUnit] = useDeleteUnitMutation();

  // A course editor can edit everything beneath it (fallbackAllowed short-circuits
  // the per-unit permission check); otherwise fall back to the unit's own grant.
  const unitPermission = useObjectPermissionState('unit', unit.id, 'editor', {
    fallbackAllowed: canEditCourse,
  });
  const canEditUnit = unitPermission.isAllowed;

  // Lazily load lessons only once the unit is expanded.
  const {
    data: lessons = [],
    error: lessonsError,
    isLoading: lessonsLoading,
  } = useGetLessonsByUnitIdQuery(unit.id, { skip: !expanded });
  const [moveLesson] = useMoveLessonMutation();

  const { setNodeRef, style, dragHandle } = dragProps;

  useEffect(() => {
    if (isActiveUnit) setExpanded(true);
  }, [isActiveUnit]);

  const sortedLessons = sortByPosition(lessons);

  const handleMoveLesson = async (lessonId: string, placement: ReorderPlacement) => {
    await moveLesson({ lessonId, unitId: unit.id, placement }).unwrap();
  };

  const handleDelete = async () => {
    setMenuAnchorEl(null);
    const confirmed = await confirm({
      title: 'Delete Unit',
      message: `Are you sure you want to delete "${unit.title}"? This also deletes its lessons and resources. This action cannot be undone.`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await deleteUnit(unit.id).unwrap();
      if (activeUnitId === unit.id) {
        void navigate(`/curriculum/courses/${courseId}`, { replace: true });
      }
    } catch (err) {
      onError(
        `Failed to delete "${unit.title}": ${extractErrorMessage(err, 'Please check your permissions and try again.')}`
      );
    }
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ position: 'relative' }}>
      <ListItemButton
        selected={isActiveUnit && !activeLessonId}
        onClick={() => {
          setExpanded(true);
          navigate(`/curriculum/courses/${courseId}/units/${unit.id}`);
        }}
        sx={{ borderRadius: 1, mb: 0.25, pl: 1, pr: canEditUnit ? 5 : 1.5 }}
      >
        {/* Expand / collapse toggle */}
        <IconButton
          size="small"
          edge="start"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-label={expanded ? 'Collapse unit' : 'Expand unit'}
          sx={{ mr: 0.25, p: 0.25 }}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        {dragHandle && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.25 }}>{dragHandle}</Box>
        )}

        {expanded ? (
          <FolderOpenOutlinedIcon
            fontSize="small"
            sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }}
          />
        ) : (
          <FolderOutlinedIcon
            fontSize="small"
            sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }}
          />
        )}

        <ListItemText
          sx={{ my: 0, minWidth: 0 }}
          disableTypography
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ minWidth: 16, fontWeight: 700, color: 'text.disabled', flexShrink: 0 }}
              >
                {index + 1}
              </Typography>
              <ClampedTextTooltip
                text={unit.title || 'Untitled unit'}
                lineClamp={2}
                sx={{ fontWeight: isActiveUnit ? 600 : 400, lineHeight: 1.35 }}
              />
            </Box>
          }
        />
      </ListItemButton>

      {canEditUnit && (
        <Box sx={{ position: 'absolute', right: 4, top: 4 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchorEl(e.currentTarget);
            }}
            aria-label="Unit options"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Lessons */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ ml: 2.5, pl: 1, borderLeft: '1px solid', borderColor: 'divider' }}>
          {lessonsLoading ? (
            <Box sx={{ pr: 1, py: 0.5 }}>
              {[1, 2].map((i) => (
                <Skeleton key={i} height={28} sx={{ mb: 0.5, borderRadius: 1 }} />
              ))}
            </Box>
          ) : lessonsError ? (
            <Alert severity="error" sx={{ my: 0.5, fontSize: '0.75rem' }}>
              Failed to load lessons.
            </Alert>
          ) : sortedLessons.length === 0 ? (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', py: 0.75, pl: 1 }}
            >
              No lessons yet
            </Typography>
          ) : (
            <SortableList
              items={sortedLessons}
              canEdit={canEditUnit}
              onMove={handleMoveLesson}
              getItemLabel={(lesson) => `Reorder ${lesson.title || 'Untitled lesson'}`}
              reorderErrorMessage="Failed to reorder lesson. Please try again."
              renderContainer={(children) => (
                <List dense disablePadding>
                  {children}
                </List>
              )}
              renderItem={(lesson, lessonDragProps) => (
                <LessonTreeNode
                  lesson={lesson}
                  index={lessonDragProps.index}
                  courseId={courseId}
                  unitId={unit.id}
                  canEdit={canEditUnit}
                  isActive={lesson.id === activeLessonId}
                  onError={onError}
                  dragProps={lessonDragProps}
                />
              )}
            />
          )}

          {/* Add lesson */}
          {canEditUnit && !lessonsLoading && (
            <ListItemButton
              onClick={() => onAddLesson(unit.id)}
              sx={{ borderRadius: 1, my: 0.25, pl: 1, color: 'text.secondary' }}
            >
              <AddIcon fontSize="small" sx={{ mr: 1, flexShrink: 0 }} />
              <ListItemText
                primary="New Lesson"
                primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
              />
            </ListItemButton>
          )}
        </Box>
      </Collapse>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        slotProps={{ paper: { elevation: 2 } }}
      >
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main', gap: 1 }}>
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

interface LessonTreeNodeProps {
  lesson: Lesson;
  index: number;
  courseId: string;
  unitId: string;
  canEdit: boolean;
  isActive: boolean;
  onError: (message: string) => void;
  dragProps: SortableListRenderProps;
}

const LessonTreeNode: React.FC<LessonTreeNodeProps> = ({
  lesson,
  index,
  courseId,
  unitId,
  canEdit,
  isActive,
  onError,
  dragProps,
}) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteLesson] = useDeleteLessonMutation();

  const { setNodeRef, style, dragHandle } = dragProps;

  const handleDelete = async () => {
    setMenuAnchorEl(null);
    const confirmed = await confirm({
      title: 'Delete Lesson',
      message: `Are you sure you want to delete "${lesson.title}"? This also deletes its resources. This action cannot be undone.`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await deleteLesson(lesson.id).unwrap();
      if (isActive) {
        void navigate(`/curriculum/courses/${courseId}/units/${unitId}`, { replace: true });
      }
    } catch (err) {
      onError(
        `Failed to delete "${lesson.title}": ${extractErrorMessage(err, 'Please check your permissions and try again.')}`
      );
    }
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ position: 'relative' }}>
      <ListItemButton
        selected={isActive}
        onClick={() =>
          navigate(`/curriculum/courses/${courseId}/units/${unitId}/lessons/${lesson.id}`)
        }
        sx={{ borderRadius: 1, mb: 0.25, pl: 1, pr: canEdit ? 5 : 1.5 }}
      >
        {dragHandle && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.25 }}>{dragHandle}</Box>
        )}
        <ArticleOutlinedIcon
          fontSize="small"
          sx={{ mr: 1, color: isActive ? 'primary.main' : 'text.disabled', flexShrink: 0 }}
        />
        <ListItemText
          sx={{ my: 0, minWidth: 0 }}
          disableTypography
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ minWidth: 16, fontWeight: 700, color: 'text.disabled', flexShrink: 0 }}
              >
                {index + 1}
              </Typography>
              <ClampedTextTooltip
                text={lesson.title || 'Untitled lesson'}
                lineClamp={2}
                sx={{ fontWeight: isActive ? 600 : 400, lineHeight: 1.35, fontSize: '0.8125rem' }}
              />
            </Box>
          }
        />
      </ListItemButton>
      {canEdit && (
        <Box sx={{ position: 'absolute', right: 4, top: 4 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchorEl(e.currentTarget);
            }}
            aria-label="Lesson options"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        slotProps={{ paper: { elevation: 2 } }}
      >
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main', gap: 1 }}>
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CurriculumNavTree;
